import {
  ArrowLeft,
  ArrowSquareOut,
  CheckCircle,
  DownloadSimple,
  Key,
  ShieldCheck,
  UploadSimple,
} from "@phosphor-icons/react";
import { Button, Label, TextInput } from "flowbite-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { APP_PLATFORM_LOGO_MAP } from "../../../../shared/constants";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Resolver } from "react-hook-form";
import AnimatedModal from "../../../../components/AnimatedModal";

export interface AppleAdsFormData {
  client_id: string;
  team_id: string;
  key_id: string;
}

interface AppleAdsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isSubmitting: boolean;
  onSubmit: (formData: AppleAdsFormData) => void;
}

const getAppleAdsSchema = (t: TFunction) =>
  yup.object().shape({
    client_id: yup
      .string()
      .required(
        t("configurations.integrations.appleAds.errors.clientIdRequired"),
      ),
    team_id: yup
      .string()
      .required(
        t("configurations.integrations.appleAds.errors.teamIdRequired"),
      ),
    key_id: yup
      .string()
      .required(t("configurations.integrations.appleAds.errors.keyIdRequired")),
  });

const AppleAdsModal: React.FC<AppleAdsModalProps> = ({
  isOpen,
  onClose,
  isSubmitting,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [downloaded, setDownloaded] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AppleAdsFormData>({
    resolver: yupResolver(getAppleAdsSchema(t)) as unknown as Resolver<AppleAdsFormData>,
    defaultValues: {
      client_id: "",
      team_id: "",
      key_id: "",
    },
  });

  const handleClose = () => {
    reset();
    setStep(1);
    setDownloaded(false);
    onClose();
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "/public-key.pem";
    link.download = "public-key.pem";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloaded(true);
  };

  const onFormSubmit = (data: AppleAdsFormData) => {
    onSubmit(data);
  };

  const ModalHeader = () => (
    <div className="flex items-center gap-3 border-gray-100">
      <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
        <img
          src={APP_PLATFORM_LOGO_MAP.app_store}
          alt="Apple Search Ads"
          className="w-8 h-8"
        />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t("configurations.integrations.apple_search_ads")}
        </h3>
        <div className="flex items-center gap-1.5 mt-0.5">
          <ShieldCheck size={13} className="text-indigo-600 flex-shrink-0" />
          <p className="text-[11px] text-gray-500">
            {t("configurations.integrations.credentialsSecure")}
          </p>
        </div>
      </div>
    </div>
  );

  const StepIndicator = () => (
    <div className="flex items-center gap-2 mb-5">
      <div
        className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${step === 1
          ? "bg-indigo-100 text-indigo-700"
          : "bg-green-100 text-green-700"
          }`}
      >
        {step === 2 ? (
          <CheckCircle size={13} weight="fill" />
        ) : (
          <span className="w-4 h-4 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px]">
            1
          </span>
        )}
        {t("configurations.integrations.appleAds.step1Label")}
      </div>
      <div className="flex-1 h-px bg-gray-200" />
      <div
        className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${step === 2
          ? "bg-indigo-100 text-indigo-700"
          : "bg-gray-100 text-gray-400"
          }`}
      >
        <span
          className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${step === 2 ? "bg-indigo-600 text-white" : "bg-gray-300 text-gray-500"
            }`}
        >
          2
        </span>
        {t("configurations.integrations.appleAds.step2Label")}
      </div>
    </div>
  );

  return (
    <AnimatedModal show={isOpen} onClose={handleClose} size="lg" title={<ModalHeader />}>
      <div className="relative">
        <StepIndicator />

        {/* Step 1: Download & Upload Public Key */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1">
                {t("configurations.integrations.appleAds.beforeYouBegin")}
              </h4>
              <p className="text-xs text-gray-500">
                {t("configurations.integrations.appleAds.downloadInstruction")}
              </p>
            </div>

            {/* Download button */}
            <div
              className={`rounded-xl border-2 border-dashed p-4 flex flex-col items-center gap-3 transition-colors ${downloaded
                ? "border-green-300 bg-green-50"
                : "border-indigo-200 bg-indigo-50"
                }`}
            >
              {downloaded ? (
                <>
                  <CheckCircle
                    size={32}
                    weight="fill"
                    className="text-green-500"
                  />
                  <p className="text-sm font-medium text-green-700">
                    {t(
                      "configurations.integrations.appleAds.downloadedSuccess",
                    )}
                  </p>
                </>
              ) : (
                <>
                  <DownloadSimple size={32} className="text-indigo-500" />
                  <p className="text-xs text-center text-gray-600">
                    {t(
                      "configurations.integrations.appleAds.downloadDescription",
                    )}
                  </p>
                </>
              )}
              <Button
                color={downloaded ? "success" : "indigo"}
                size="sm"
                onClick={handleDownload}
              >
                <div className="flex items-center gap-2">
                  <DownloadSimple size={15} />
                  {t("configurations.integrations.appleAds.downloadPem")}
                </div>
              </Button>
            </div>

            {/* Upload instructions */}
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <UploadSimple size={16} className="text-blue-600 flex-shrink-0" />
                <p className="text-xs font-semibold text-blue-800">
                  {t(
                    "configurations.integrations.appleAds.uploadInstructionTitle",
                  )}
                </p>
              </div>
              <ol className="list-decimal list-inside space-y-1 text-xs text-blue-700 pl-1">
                <li>
                  {t(
                    "configurations.integrations.appleAds.uploadStep1",
                  )}
                </li>
                <li>
                  {t(
                    "configurations.integrations.appleAds.uploadStep2",
                  )}
                </li>
                <li>
                  {t(
                    "configurations.integrations.appleAds.uploadStep3",
                  )}
                </li>
              </ol>
              <a
                href="https://app.searchads.apple.com/cm/app/settings/apicertificates"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 font-medium mt-1"
              >
                <ArrowSquareOut size={12} />
                {t(
                  "configurations.integrations.appleAds.openApiCertificates",
                )}
              </a>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <Button color="light" onClick={handleClose}>
                {t("common.cancel")}
              </Button>
              <Button
                color="indigo"
                onClick={() => setStep(2)}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} />
                  {t("configurations.integrations.appleAds.idsGenerated")}
                </div>
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Enter IDs */}
        {step === 2 && (
          <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1">
                {t("configurations.integrations.appleAds.enterIdsTitle")}
              </h4>
              <p className="text-xs text-gray-500">
                {t("configurations.integrations.appleAds.enterIdsDesc")}
              </p>
            </div>

            {/* Client ID */}
            <div>
              <Label
                htmlFor="client_id"
                className="flex items-center gap-1 mb-2"
              >
                <Key size={14} className="text-indigo-600" />
                {t("configurations.integrations.appleAds.clientId")}
              </Label>
              <TextInput
                id="client_id"
                placeholder="SEARCHADS.ad81b2c4-e1f9-4672-9abc-d45e67f891b2"
                {...register("client_id")}
                color={errors.client_id ? "failure" : undefined}
              />
              {errors.client_id && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.client_id.message}
                </p>
              )}
            </div>

            {/* Team ID */}
            <div>
              <Label
                htmlFor="team_id"
                className="flex items-center gap-1 mb-2"
              >
                <Key size={14} className="text-indigo-600" />
                {t("configurations.integrations.appleAds.teamId")}
              </Label>
              <TextInput
                id="team_id"
                placeholder="SEARCHADS.9f2e4a6d-bc81-4d32-8e1f-0b9c7a5d3e1f"
                {...register("team_id")}
                color={errors.team_id ? "failure" : undefined}
              />
              {errors.team_id && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.team_id.message}
                </p>
              )}
            </div>

            {/* Key ID */}
            <div>
              <Label
                htmlFor="key_id"
                className="flex items-center gap-1 mb-2"
              >
                <Key size={14} className="text-indigo-600" />
                {t("configurations.integrations.appleAds.keyId")}
              </Label>
              <TextInput
                id="key_id"
                placeholder="8a2d1e9c-4f7b-4d6e-9c1a-2b3d4e5f6g7h"
                {...register("key_id")}
                color={errors.key_id ? "failure" : undefined}
              />
              {errors.key_id && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.key_id.message}
                </p>
              )}
            </div>

            {/* Note Banner */}
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 leading-relaxed">
              <p className="font-semibold mb-1">{t("validation.importantNote")}</p>
              {t("configurations.integrations.appleAdsNote")}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <Button
                color="light"
                onClick={() => setStep(1)}
                disabled={isSubmitting}
              >
                <div className="flex items-center gap-1.5">
                  <ArrowLeft size={14} />
                  {t("common.back")}
                </div>
              </Button>
              <Button
                color="indigo"
                type="submit"
                disabled={isSubmitting}
                isProcessing={isSubmitting}
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck size={16} />
                  {t("configurations.integrations.connectServiceAccount")}
                </div>
              </Button>
            </div>
          </form>
        )}
      </div>
    </AnimatedModal>
  );
};

export default AppleAdsModal;
